import { useOrdersSignalR } from '@/features/orders/hooks/useOrdersSignalR';
import {
  CANCELLATION_REQUESTS_ALERT_SOURCE_ID,
  useCancellationRequestsAlertState,
} from '@/features/orders/hooks/useCancellationRequestsAlertState';
import {
  AMENDED_ON_THE_ROAD_ALERT_SOURCE_ID,
  dismissAmendedOnTheRoadNotices,
  useAmendedOnTheRoadAlertState,
} from '@/features/orders/hooks/useAmendedOnTheRoadAlertState';
import { CONVERSATIONS_READ_PERMISSION, useConversationsAccess } from '../hooks/useConversationsAccess';
import { useConversationsSummary, useCustomerAgentStatus } from '../hooks/useConversations';
import { playHandoffAlert } from './handoff-alert-sound';
import { reasonLabel } from './reason-labels';
import { useTick } from './use-media-query';
import { elapsedMs, formatWaitPhrase, serverNow } from './wait-time';
import type { ConversationSummary } from '../types/conversations.types';

/* ==========================================================================
   Fontes dos alertas globais (change customer-agent-handoff, D19)

   O `ConversationAlerts` não é amarrado ao resumo de conversas: ele percorre
   esta lista, e cada fonte diz quantas pendências há, qual é a mais nova e o
   que escrever na faixa e na notificação (já sem nome, telefone ou texto do
   cliente). A aba líder, o som, o silêncio, o título e a notificação são do
   `ConversationAlerts` e valem para todas: o título soma as contagens das
   fontes visíveis, o som repete enquanto alguma tem contagem, e cada fonte
   tem a sua linha na faixa.

   Fonte nova (as solicitações de pedido do cliente, por exemplo) entra aqui,
   em `ALERT_SOURCES`, com a permissão de quem a vê. Nunca um segundo
   `ConversationAlerts`: dois tocariam em dobro e disputariam o título. A
   disponibilidade da área (`useCustomerAgentAvailable` com a permissão da
   fonte) vale para todas, porque todas são do Atendente IA.
   ========================================================================== */

export interface AlertSourceLink {
  to: string;
  label: string;
}

export interface AlertSourceNotification {
  title: string;
  /** Só o motivo e a prioridade: nunca nome, telefone ou texto. */
  body: string;
  /** Uma notificação por pendência (o navegador troca a de mesma `tag`). */
  tag: string;
  /** Para onde o clique leva. */
  href: string;
}

export interface AlertSourceState {
  /** Pendências que abrem a linha da faixa, o título e o som; 0 esconde a linha. */
  count: number;
  highPriorityCount: number;
  /**
   * Marca monotônica da pendência mais nova (o instante do alerta, em ms): há
   * pendência nova (toca e notifica) só quando ela passa a maior já vista pela
   * fonte. Resolver a mais nova, com outra mais antiga ainda pendente, faz a
   * marca voltar para a da antiga, e isso não é pendência nova.
   */
  latestMark: number | null;
  /** A linha da faixa. */
  line: string;
  /**
   * O texto curto do resumo dos avisos do topo (decisão D3), quando a linha é
   * longa demais para ele; sem ele, o resumo usa a linha.
   */
  summary?: string | null;
  /** Explicação acrescentada à linha (o atendente desligado, por exemplo). */
  note: string | null;
  link: AlertSourceLink;
  /**
   * O botão que encerra o aviso nesta aba ("Entendi"), só nas fontes com
   * `dismiss`; ausente nas que somem sozinhas.
   */
  dismissLabel?: string | null;
  notification: AlertSourceNotification | null;
}

export interface AlertSource {
  id: string;
  /** A claim que vê a fonte; sem ela, a fonte nem é consultada. */
  permission: string;
  /**
   * O estado da fonte, ou nulo enquanto ela não sabe (carregando) ou quando
   * ela se esconde em silêncio (o 403 de token antigo). Só é chamado com a
   * permissão e a área disponíveis.
   */
  useAlertState: () => AlertSourceState | null;
  /** O som da pendência; o do atendimento, por padrão. Devolve se tocou. */
  sound?: () => boolean;
  /** Falso: o som toca só na chegada, sem a repetição de 60 s (um aviso, e não uma pendência). */
  repeats?: boolean;
  /** Encerra o aviso da fonte nesta aba, pelo botão `dismissLabel` da faixa. */
  dismiss?: () => void;
}

/* ---- Fonte `conversations`: clientes esperando a equipe ---- */

export const CONVERSATIONS_ALERT_LINK: AlertSourceLink = { to: '/conversations', label: 'Ver conversas' };

export const ATTENDANT_OFF_ALERT_NOTE =
  'O Atendente IA está desligado. Responda pelo WhatsApp do celular e clique Assumir para parar o aviso.';

export const HANDOFF_NOTIFICATION_TITLE = 'Cliente esperando no WhatsApp';

/**
 * "1 cliente esperando no WhatsApp · há 3 min" ou "3 clientes esperando no
 * WhatsApp · 1 prioritário · há 7 min" (relatório de UX, P1-12): o número, a
 * prioridade e a espera do mais antigo, contada do alerta contra o relógio
 * do servidor.
 */
export function conversationsAlertLine(summary: ConversationSummary, now: number): string {
  const wait = formatWaitPhrase(elapsedMs(summary.oldestAlertedAt, now));
  const parts = [
    summary.awaiting === 1 ? '1 cliente esperando no WhatsApp' : `${summary.awaiting} clientes esperando no WhatsApp`,
  ];
  if (summary.awaitingHighPriority > 0) {
    parts.push(`${summary.awaitingHighPriority} ${summary.awaitingHighPriority === 1 ? 'prioritário' : 'prioritários'}`);
  }
  parts.push(wait);
  return parts.join(' · ');
}

/** "3 clientes esperando · 1 prioritário · há 7 min": a linha sem o canal, para o resumo do topo. */
export function conversationsAlertSummary(summary: ConversationSummary, now: number): string {
  return conversationsAlertLine(summary, now).replace(' esperando no WhatsApp', ' esperando');
}

/** "Motivo: Alergia grave · Prioritário": só o motivo e a prioridade. */
export function handoffNotificationBody(reason: string, priority: string): string {
  return `Motivo: ${reasonLabel(reason)}${priority === 'high' ? ' · Prioritário' : ''}`;
}

/** O estado da fonte a partir do resumo; só as conversas alertadas contam (a adiada não abre faixa). */
export function conversationsAlertState(
  summary: ConversationSummary,
  now: number,
  attendantDisabled: boolean,
): AlertSourceState {
  const latest = summary.awaiting > 0 ? summary.latestAlerted : null;
  const latestAt = latest ? Date.parse(latest.alertedAt) : Number.NaN;
  return {
    count: summary.awaiting,
    highPriorityCount: summary.awaitingHighPriority,
    latestMark: Number.isFinite(latestAt) ? latestAt : null,
    line: summary.awaiting > 0 ? conversationsAlertLine(summary, now) : '',
    summary: summary.awaiting > 0 ? conversationsAlertSummary(summary, now) : null,
    note: summary.awaiting > 0 && attendantDisabled ? ATTENDANT_OFF_ALERT_NOTE : null,
    link: CONVERSATIONS_ALERT_LINK,
    notification: latest
      ? {
          title: HANDOFF_NOTIFICATION_TITLE,
          body: handoffNotificationBody(latest.reason, latest.priority),
          tag: `conversation-${latest.conversationId}`,
          href: `/conversations/${encodeURIComponent(latest.conversationId)}`,
        }
      : null,
  };
}

function useConversationsAlertState(): AlertSourceState | null {
  // A conexão de tempo real é a compartilhada de pedidos: as campainhas de
  // conversa revalidam o resumo, e a faixa a mantém aberta em qualquer tela.
  useOrdersSignalR();
  // Recontar "há N min" sem nova leitura.
  useTick(30_000);
  const { available } = useConversationsAccess();
  const summary = useConversationsSummary();
  const status = useCustomerAgentStatus();
  if (!available || !summary.data) return null;
  const now = serverNow({ serverTime: summary.data.serverTime, receivedAt: summary.dataUpdatedAt });
  return conversationsAlertState(summary.data, now, status.data?.state === 'disabled');
}

export const CONVERSATIONS_ALERT_SOURCE: AlertSource = {
  id: 'conversations',
  permission: CONVERSATIONS_READ_PERMISSION,
  useAlertState: useConversationsAlertState,
  sound: playHandoffAlert,
};

/* ---- Fonte `cancellation_requests`: o cliente pediu o cancelamento ----
   Change orders-customer-cancellation (D22): as aguardando no prazo e as
   vencidas sem a equipe chamada, para quem decide (`orders.update_status`),
   com o som do atendimento. O estado e os textos são do módulo de pedidos. */

export const CANCELLATION_REQUESTS_ALERT_SOURCE: AlertSource = {
  id: CANCELLATION_REQUESTS_ALERT_SOURCE_ID,
  permission: 'orders.update_status',
  useAlertState: useCancellationRequestsAlertState,
};

/* ---- Fonte `amended_on_the_road`: pagamento trocado com o pedido na rua ----
   Change orders-customer-amendments (decisão 7): a alteração de pagamento ou
   troco aplicada com o pedido em rota (`order.amended` com
   `deliveryAlreadyOut`), para quem vê pedidos (`orders.read`), com o som de
   solicitação uma vez, sem repetição, até "Entendi" ou o pedido sair da rua.
   O estado e os textos são do módulo de pedidos. */

export const AMENDED_ON_THE_ROAD_ALERT_SOURCE: AlertSource = {
  id: AMENDED_ON_THE_ROAD_ALERT_SOURCE_ID,
  permission: 'orders.read',
  useAlertState: useAmendedOnTheRoadAlertState,
  repeats: false,
  dismiss: dismissAmendedOnTheRoadNotices,
};

/** As fontes registradas, na ordem das linhas da faixa. */
export const ALERT_SOURCES: readonly AlertSource[] = [
  CONVERSATIONS_ALERT_SOURCE,
  CANCELLATION_REQUESTS_ALERT_SOURCE,
  AMENDED_ON_THE_ROAD_ALERT_SOURCE,
];
